import { AlertCircle, CheckCircle2, Clock, XCircle } from "lucide-react";
import { Badge } from "../badge";
import { cn } from "../../../lib/utils";
import { Tooltip, TooltipContent, TooltipTrigger } from "../tooltip";

export type StatusTone =
  | "active"
  | "inactive"
  | "deleted"
  | "trial"
  | "warning"
  | "muted";

export interface StatusBadgeProps {
  tone: StatusTone;
  label: string;
  withIcon?: boolean;
  tooltip?: string;
  className?: string;
}

const TONE_CLASSES: Record<StatusTone, string> = {
  active:
    "bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100",
  inactive:
    "bg-muted text-muted-foreground border-border",
  deleted:
    "bg-destructive/10 text-destructive border-destructive/20",
  trial:
    "bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100",
  warning:
    "bg-amber-50 text-amber-700 border-amber-200 hover:bg-amber-100",
  muted:
    "bg-muted/60 text-muted-foreground border-border",
};

const ICON_BY_TONE: Record<StatusTone, React.ComponentType<{ className?: string }>> = {
  active: CheckCircle2,
  inactive: AlertCircle,
  deleted: XCircle,
  trial: Clock,
  warning: AlertCircle,
  muted: AlertCircle,
};

export function StatusBadge({
  tone,
  label,
  withIcon = false,
  tooltip,
  className,
}: StatusBadgeProps) {
  const Icon = ICON_BY_TONE[tone];
  const badge = (
    <Badge variant="outline" className={cn(TONE_CLASSES[tone], className)}>
      {withIcon && <Icon className="mr-1 h-3 w-3" />}
      {label}
    </Badge>
  );
  if (!tooltip) return badge;
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="inline-flex cursor-help">{badge}</span>
      </TooltipTrigger>
      <TooltipContent>{tooltip}</TooltipContent>
    </Tooltip>
  );
}
